/// <amd-module name="Build/Designer/PipelineVariableEditPanel" />
import * as React from "react";

import * as CommonResources from "Build/Common/Library/Resources";
import { FwLinks } from "Build/Designer/Constants";
import * as Resources from "Build/Designer/Resources";

import { format } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { VssComponent } from "VSS/Platform/Layout";

import { Button } from "VSSUI/Button";
import { ButtonGroup } from "VSSUI/ButtonGroup";
import { Checkbox } from "VSSUI/Checkbox";
import { FormItem } from "VSSUI/FormItem";
import { TitleSize } from "VSSUI/Header";
import { Link } from "VSSUI/Link";
import { CustomPanel, PanelContent, PanelFooter, PanelHeader } from "VSSUI/Panel";
import { TextField } from "VSSUI/TextField";

interface PipelineVariableEditProps {
    variable: PipelineVariableInfo;
    allVariables: PipelineVariableInfo[];
    onDismiss: () => void;
    onUpdate: (variable: PipelineVariableInfo) => void;
}

export interface PipelineVariableInfo {
    key: string,
    name: string,
    value: string | null,
    isSecret?: boolean,
    allowOverride?: boolean,
    isNew: boolean,
    isDeleted: boolean,
    isDirty?: boolean,
    isSecretDirty?: boolean
}

interface PipelineVariableEditState extends PipelineVariableInfo {
}

export class PipelineVariableEditPanel extends VssComponent<PipelineVariableEditProps, PipelineVariableEditState> {
    private _otherVariableNames: { [name: string]: boolean };

    constructor(props: PipelineVariableEditProps) {
        super(props);
        const state = { ...props.variable };
        state.isDirty = false;
        state.name = state.name || "";
        state.value = state.value || "";
        this.state = state;

        this._otherVariableNames = {};
        this.props.allVariables.forEach(variable => { this._otherVariableNames[variable.name.toLocaleLowerCase()] = true });
        if (this.props.variable.name) {
            this._otherVariableNames[this.props.variable.name.toLocaleLowerCase()] = false;
        }
    }

    public componentDidUpdate(prevProps: Readonly<PipelineVariableEditProps>, prevState: Readonly<PipelineVariableEditState>) {
        super.componentDidUpdate(prevProps, prevState);
        const isDirty = this._isDirty();
        if (this.state.isDirty !== isDirty) {
            this.setState({ isDirty });
        }
    }

    public render(): JSX.Element {
        const titleText = this.props.variable.isNew && !this.props.variable.name
            ? Resources.VariableEditPanelTitleNew : Resources.VariableEditPanelTitleUpdate;

        return (
            <CustomPanel
                ariaLabel={titleText}
                className={"pipeline-editor-variable-edit-panel"}
                calloutClassName={"pipeline-editor-variable-edit-callout"}
                defaultActiveElement={this.state.name ? ".variable-value-container input" : ".variable-name-container input"}
                onDismiss={this.props.onDismiss}
                lightDismiss={!this.state.isDirty}>
                <PanelHeader
                    backButtonProps={{ onClick: this.props.onDismiss }}
                    titleProps={{ text: titleText, size: TitleSize.Large }}
                    onDismiss={this.props.onDismiss}
                    showCloseButton={false} />
                <PanelContent>
                    {this._renderBody()}
                </PanelContent>
                {this._renderFooter()}
            </CustomPanel>
        );
    }

    private _renderBody(): JSX.Element {
        const isExistingSecret = this.props.variable.isSecret; // secret that already exists but may not be saved yet
        const isSavedExistingSecret = isExistingSecret && !this.props.variable.isNew && !this.props.variable.isSecretDirty; // secret that already exists and has been saved
        let nameError = "";
        const variableReferenceSyntax = "$(" + (this.state.name || Resources.VariableReferencePlaceholderName) + ")";
        const environmentVariableName = (this.state.name || Resources.VariableReferencePlaceholderName).toLocaleUpperCase().replace(/[ \.]/g, "_");
        if (this._isDuplicateName()) {
            const duplicate = this._getDuplicateVariable();
            nameError = duplicate ? format(Resources.VariableEditPanelIsDuplicate, duplicate.name) : "";
        }
        return (
            <div className="flex-grow">
                <FormItem
                    className={"variable-edit-name-container"}
                    label={Resources.Name}
                    message={nameError}
                    error={!!nameError}>
                    <TextField
                        value={this.state.name}
                        disabled={isSavedExistingSecret}
                        className={"variable-name-container"}
                        required={true}
                        onChange={this._onNameChanged} />
                </FormItem>
                <FormItem
                    label={Resources.Value}>
                    <TextField
                        value={this.state.value || ""}
                        className={"variable-value-container"}
                        inputType={this.state.isSecret ? "password" : "text"}
                        placeholder={isExistingSecret && this.state.isSecret && !this.state.value ? CommonResources.VariableSecretMask : undefined}
                        onChange={this._onValueChanged} />
                </FormItem>
                <Checkbox
                    className={"variable-edit-checkbox first-checkbox"}
                    label={Resources.VariableEditPanelIsSecret}
                    checked={this.state.isSecret}
                    onChange={this._onIsSecretChanged}
                    disabled={isSavedExistingSecret}
                    tooltipProps={isSavedExistingSecret ? { text: Resources.DisabledSecretCheckboxTooltip } : undefined} />
                <Checkbox
                    className={"variable-edit-checkbox"}
                    label={Resources.VariableEditPanelAllowOverride}
                    checked={this.state.allowOverride}
                    onChange={this._onAllowOverrideChanged} />
                <div className="body-m variable-help-container">
                    <div className={"variable-help-section"}>
                        <div className={"variable-reference-text"}>
                            <FormatComponent format={Resources.VariableEditPanelToReferenceText}>
                                <span className="font-weight-semibold">{Resources.VariableEditPanelToReferenceText0}</span>
                                <span className="code-format">{variableReferenceSyntax}</span>
                            </FormatComponent>
                        </div>
                    </div>
                    <div className={"variable-help-section"}>
                        <div>
                            <FormatComponent format={Resources.VariableEditPanelToUseText}>
                                <span className="font-weight-semibold">{Resources.VariableEditPanelToUseText0}</span>
                            </FormatComponent>
                        </div>
                        <div>
                            <FormatComponent format={Resources.VariableEditPanelToUseTextMore}>
                                <span className="code-format">{"."}</span>
                                <span className="code-format">{"_"}</span>
                            </FormatComponent>
                        </div>
                    </div>
                    <div className={"variable-help-section"}>
                        <div>
                            <FormatComponent format={Resources.VariableEditPanelToUseExBatch}>
                                <span className="code-format">{"%" + environmentVariableName + "%"}</span>
                            </FormatComponent>
                        </div>
                        <div>
                            <FormatComponent format={Resources.VariableEditPanelToUseExPowerShell}>
                                <span className="code-format">{"${env:" + environmentVariableName + "}"}</span>
                            </FormatComponent>
                        </div>
                        <div>
                            <FormatComponent format={Resources.VariableEditPanelToUseExBash}>
                                <span className="code-format">{"$(" + environmentVariableName + ")"}</span>
                            </FormatComponent>
                        </div>
                    </div>
                    {this.state.isSecret &&
                        <div className={"variable-help-section"}>
                            <div>
                                <FormatComponent format={Resources.VariableEditPanelToUseSecret}>
                                    <span className="font-weight-semibold">{Resources.VariableEditPanelToUseSecret0}</span>
                                </FormatComponent>
                            </div>
                        </div>
                    }
                </div>
            </div>
        );
    }

    private _renderFooter(): JSX.Element {
        return (
            <PanelFooter
                className="flex-row flex-center"
                showSeparator={true}>
                <Link
                    className="flex-grow"
                    href={FwLinks.LearnMoreVariables}
                    target={"_blank"}
                    rel={"noopener noreferrer"}>
                    {Resources.VariablesPanelLearnMore}
                </Link>

                <ButtonGroup className="flex-grow justify-end">
                    <Button
                        key="cancel"
                        text={Resources.Cancel}
                        onClick={this.props.onDismiss} />
                    <Button
                        key="ok"
                        text={Resources.OK}
                        primary={true}
                        disabled={!this.state.isDirty || this._isEmptyName() || this._isDuplicateName()}
                        onClick={this._onUpdate} />
                </ButtonGroup>
            </PanelFooter>
        );
    }

    private _onNameChanged = (_event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, name: string) => {
        this.setState({ name });
    }

    private _onValueChanged = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => {
        this.setState({ value });
    }

    private _onIsSecretChanged = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, checked: boolean) => {
        this.setState({ isSecret: checked || undefined });
    }

    private _onAllowOverrideChanged = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, checked: boolean) => {
        this.setState({ allowOverride: checked || undefined });
    }

    private _onUpdate = () => {
        const variable = { ...this.state };
        variable.name = variable.name.trim();

        // secret values must be null (unchanged) or a non-empty string for a new value.
        variable.value = variable.isSecret ? variable.value || null : variable.value;
        this.props.onUpdate(variable);
    }

    private _isDirty(): boolean {
        const original = this.props.variable || {} as PipelineVariableInfo;
        return this.state.name !== (original.name || "")
            || this.state.value !== (original.value || "")
            || this.state.isSecret !== original.isSecret
            || this.state.allowOverride !== original.allowOverride;
    }

    private _isEmptyName() {
        return !this.state.name.trim();
    }

    private _isDuplicateName(): boolean {
        const name = this.state.name.trim().toLocaleLowerCase();
        return !!(name && this._otherVariableNames[name]);
    }

    private _getDuplicateVariable(): PipelineVariableInfo | undefined {
        const name = this.state.name.trim().toLocaleLowerCase();
        return this.props.allVariables.find(variable => variable.name.toLocaleLowerCase() === name);
    }

}

